import { useMemo } from 'react';
import { BarChart } from '@mantine/charts';
import { formatPrice } from '@shared/utils';
import './StatsBarChart.css';

/** Как на странице «Заказы по категориям»: ширина холста и шаг по категориям. */
const CHART_MIN_WIDTH_BASE = 400;
const CHART_WIDTH_PER_GROUP = 48;
const CHART_AXIS_GUTTER = 44;

export const STATS_BAR_CHART_HEIGHT_PX = 260;

/** Ось выручки, как на главной селлера: тысячи рублей, например 3,5т */
function formatRevenueAxisThousands(v: number): string {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    if (n === 0) return '0т';
    const k = n / 1000;
    const roundedTenth = Math.round(k * 10) / 10;
    if (Math.abs(roundedTenth - Math.round(roundedTenth)) < 1e-6) {
        return `${Math.round(roundedTenth)}т`;
    }
    return `${roundedTenth.toFixed(1).replace('.', ',')}т`;
}

export interface StatsBarChartSeriesConfig {
    /** Ключ поля в `data` (совпадает с `name` в series Mantine) */
    dataKey: string;
    /** Подпись в легенде графика */
    legendLabel: string;
    /** Заголовок строки в тултипе */
    tooltipTitle: string;
}

export interface StatsBarChartProps {
    /** Строки графика: категория по `categoryKey` + два числовых поля */
    data: Array<Record<string, string | number>>;
    /** Поле подписи по оси X */
    categoryKey: string;
    /** Левая ось — деньги (форматирование как выручка) */
    moneySeries: StatsBarChartSeriesConfig;
    /** Правая ось — целое число, в тултипе «… шт.» */
    countSeries: StatsBarChartSeriesConfig;
}

export function StatsBarChart({ data, categoryKey, moneySeries, countSeries }: StatsBarChartProps) {
    const chartMinWidthPx = useMemo(() => {
        const n = data.length;
        if (n === 0) return CHART_MIN_WIDTH_BASE;
        return Math.max(CHART_MIN_WIDTH_BASE, n * CHART_WIDTH_PER_GROUP + CHART_AXIS_GUTTER);
    }, [data.length]);

    return (
        <div className="stats-bar-chart__scroll">
            <div className="stats-bar-chart__inner" style={{ width: chartMinWidthPx }}>
                <BarChart
                    h={STATS_BAR_CHART_HEIGHT_PX}
                    w="100%"
                    maxBarWidth={22}
                    data={data}
                    dataKey={categoryKey}
                    series={[
                        {
                            name: moneySeries.dataKey,
                            color: 'pink.6',
                            label: moneySeries.legendLabel,
                            yAxisId: 'left',
                        },
                        {
                            name: countSeries.dataKey,
                            color: 'cyan.6',
                            label: countSeries.legendLabel,
                            yAxisId: 'right',
                        },
                    ]}
                    tickLine="xy"
                    gridAxis="xy"
                    strokeDasharray="4 4"
                    gridColor="gray.2"
                    withLegend
                    legendProps={{ wrapperStyle: { fontSize: 12, paddingTop: 4, color: '#737373' } }}
                    withRightYAxis
                    barChartProps={{
                        margin: { top: 4, right: 4, bottom: 28, left: 4 },
                    }}
                    yAxisProps={{
                        width: 40,
                        tick: { fontSize: 12, fill: '#737373' },
                        tickFormatter: formatRevenueAxisThousands,
                    }}
                    rightYAxisProps={{
                        width: 32,
                        tick: { fontSize: 12, fill: '#737373' },
                        allowDecimals: false,
                        tickFormatter: (v: number) => String(Math.round(Number(v))),
                    }}
                    xAxisProps={{
                        angle: -35,
                        textAnchor: 'end',
                        height: 56,
                        interval: 0,
                        tick: {
                            fontSize: 12,
                            fill: '#737373',
                            transform: 'translate(0, 6)',
                        },
                    }}
                    tooltipProps={{
                        content: ({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            return (
                                <div className="stats-bar-chart__tooltip">
                                    {label != null && label !== '' && (
                                        <div className="stats-bar-chart__tooltip-title">
                                            {String(label)}
                                        </div>
                                    )}
                                    {payload.map((item) => {
                                        const name = String(item.name ?? item.dataKey ?? '');
                                        const val = item.value as number;
                                        const isMoney = name === moneySeries.dataKey;
                                        const text = isMoney
                                            ? formatPrice(Number(val))
                                            : `${Math.round(Number(val))} шт.`;
                                        const title = isMoney
                                            ? moneySeries.tooltipTitle
                                            : countSeries.tooltipTitle;
                                        return (
                                            <div key={name} className="stats-bar-chart__tooltip-row">
                                                <span>{title}</span>
                                                <span>{text}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        },
                    }}
                />
            </div>
        </div>
    );
}
